// Agents › Runs (`/c/{slug}/agents/runs`, `/agents/runs/{id}`; spec 06 screen 10; M3-08): what the
// digital employees did and are doing. The list filters by state (`?state=`, the API's filter);
// each row says what woke the agent, how long it ran and what it cost. The detail is the run's
// step timeline (rec 7: one collapsed list, payloads on demand, agent-audience payloads stay
// hidden), its verdict, cost chip, hand-back and wake-up source, and Stop for a live run
// (`runs.cancel`: releases or settles its reservation). Take-over belongs to agent rooms
// (`channels/topics …/take-over`, 04), so a run has Stop only.
// DC-07 (ADR-0019; AGT-UI-2): the verdict with its evidence and output pointers, the decision log
// (the run detail inlines its first 100 rows; "Show the whole log" pages `runs.decisions` on
// demand), and for a run waiting for review a notice saying why — a premise escalation lists the
// run's failed decisions — with the supervisor's Resume.
// MONITORING-P1 (run-telemetry.tsx): the host's events join the steps timeline, a card per
// detached job, Stop on host next to Stop run, its requests, and the run's open alerts.
// Calls on load: the runs page (or the run and its telemetry), plus the cached DE list and
// company detail.
import { ApiError } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AvatarTile,
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  EmptyState,
  InfoTip,
  Notice,
  PageHeader,
  SettingsSection,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ViewTabs,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ActivityIcon,
  BrainIcon,
  ChevronDownIcon,
  FileCheckIcon,
  type LucideIcon,
  OctagonXIcon,
  StickyNoteIcon,
  WrenchIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { RunDetail, RunRow, RunStepRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { NotFoundPage } from "../../pages/status-pages";
import { useWalletOn } from "../billing/use-wallet-on";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { digitalEmployeesQuery } from "../queries";
import { Facts, LoadError, Loading, LoadMore, Section } from "../screen-parts";
import {
  CostChip,
  formatElapsed,
  formatMicros,
  formatTokens,
  isLive,
  RUN_STATE_LABELS,
  type RunState,
  RunStateBadge,
  usageUnreported,
  useCostCurrency,
  wakeupSource,
} from "./agent-parts";
import {
  approvalQuery,
  invalidateAgents,
  runDecisionsQuery,
  runQuery,
  runsQuery,
} from "./agent-queries";
import { ContinueRunButton, failureStopped } from "./continue-run";
import { mayOfferResume, needsReview, REVIEW_REASON_LABELS, ResumeRunButton } from "./resume-run";
import {
  EventItem,
  mergeTimeline,
  RunAlerts,
  RunJobs,
  type RunTelemetry,
  StopOnHostButton,
  StopRequests,
  stopIsLive,
  useRunTelemetry,
} from "./run-telemetry";

/** The list's filters, in the order of its tabs (`all` = no filter). */
export const RUN_FILTERS = [
  "all",
  "running",
  "queued",
  "awaiting_approval",
  "completed",
  "failed",
  "cancelled",
] as const;
export type RunFilterKey = (typeof RUN_FILTERS)[number];

/** DE names by membership (the sidebar's cached list: no extra call). */
export function useDeNames(api: Api, co: string): Map<string, string> {
  const des = useInfiniteQuery(digitalEmployeesQuery(api, co));
  return useMemo(
    () =>
      new Map(
        (des.data?.pages ?? [])
          .flatMap((p) => p.data)
          // WORKFORCE: without a "DE · " label (the screens badge the name instead).
          .map((d) => [d.membership_id, workerName(d.display_name)]),
      ),
    [des.data],
  );
}

export function RunsScreen({
  api,
  company,
  filter,
  onFilter,
}: {
  api: Api;
  company: CompanyAccess;
  filter: RunFilterKey;
  onFilter: (next: RunFilterKey) => void;
}) {
  return (
    <>
      <PageHeader
        spacing="tabs"
        eyebrow={
          // UX pass 1.1: Runs is reached from Activity (kept until Andrei decides on the swap).
          <Link
            to="/c/$slug/agents/activity"
            params={{ slug: company.slug }}
            search={keepFocus}
            className="underline-offset-4 hover:underline"
          >
            Activity
          </Link>
        }
        title="Runs"
        badge={
          <InfoTip label="About Runs">
            Every run of your digital employees: what woke it, what it did, what it cost.
          </InfoTip>
        }
      />
      <ViewTabs
        aria-label="Run state"
        value={filter}
        onValueChange={(v) => onFilter(v as RunFilterKey)}
        tabs={RUN_FILTERS.map((f) => ({
          value: f,
          label: f === "all" ? "All" : RUN_STATE_LABELS[f],
        }))}
      />
      <div className="pt-5">
        <RunsTable
          api={api}
          company={company}
          state={filter === "all" ? undefined : filter}
          caption="Runs, newest first."
        />
      </div>
    </>
  );
}

/** A runs table (the Runs page, and a DE profile's Runs tab with `membershipId`). */
export function RunsTable({
  api,
  company,
  state,
  membershipId,
  caption,
}: {
  api: Api;
  company: CompanyAccess;
  state?: RunState | undefined;
  membershipId?: string;
  caption: string;
}) {
  const runs = useInfiniteQuery(
    runsQuery(api, company.id, {
      ...(state ? { state } : {}),
      ...(membershipId ? { membership_id: membershipId } : {}),
    }),
  );
  const names = useDeNames(api, company.id);
  const currency = useCostCurrency(api, company.id);
  const walletOn = useWalletOn(api, company.id);
  const rows = runs.data?.pages.flatMap((p) => p.data) ?? [];
  if (runs.isPending) return <Loading label="Loading runs" rows={4} />;
  if (runs.isError) return <LoadError what="Runs" error={runs.error} />;
  if (!rows.length)
    return (
      <EmptyState
        variant="card"
        icon={ActivityIcon}
        title={state ? `No ${RUN_STATE_LABELS[state].toLowerCase()} runs` : "No runs yet"}
        description="A run starts when something wakes a digital employee: a mention, a task on its seat, its heartbeat or an approval decided."
      />
    );
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <Table data-slot="runs-table">
        <TableCaption>{caption}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Run</TableHead>
            <TableHead scope="col">Woken by</TableHead>
            <TableHead scope="col">State</TableHead>
            <TableHead scope="col">Started</TableHead>
            <TableHead scope="col">Duration</TableHead>
            <TableHead scope="col">Cost</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} data-run={r.id}>
              <TableHead scope="row" className="font-medium">
                <Link
                  to="/c/$slug/agents/runs/$id"
                  params={{ slug: company.slug, id: r.id }}
                  search={keepFocus}
                  className="flex items-center gap-2.5 underline-offset-4 hover:underline"
                >
                  <AvatarTile
                    name={names.get(r.membership_id) ?? "DE"}
                    kind="agent"
                    size="sm"
                    aria-hidden="true"
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="inline-flex min-w-0 items-center gap-1.5">
                      <span className="truncate">
                        {names.get(r.membership_id) ?? "Digital employee"}
                      </span>
                      <WorkerBadge kind="de" size="sm" />
                    </span>
                    <span className="font-mono text-2xs text-muted-foreground">
                      {r.id.slice(-8)}
                    </span>
                  </span>
                </Link>
              </TableHead>
              <TableCell className="text-muted-foreground">
                {wakeupSource(r.trigger_kind, r.trigger_ref)}
              </TableCell>
              <TableCell>
                <RunStateBadge state={r.state} />
              </TableCell>
              <TableCell className="text-muted-foreground tabular-nums">
                {r.started_at ? formatDateTime(r.started_at) : "Not started"}
              </TableCell>
              <TableCell className="tabular-nums">
                {r.started_at ? formatElapsed(r.started_at, r.ended_at) : "—"}
              </TableCell>
              <TableCell>
                <CostChip run={r} currency={currency} hideTokens={walletOn} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <LoadMore
        what="runs"
        hasMore={runs.hasNextPage}
        loading={runs.isFetchingNextPage}
        onMore={() => void runs.fetchNextPage()}
      />
    </div>
  );
}

const STEP_ICONS: Record<RunStepRow["kind"], LucideIcon> = {
  model_call: BrainIcon,
  tool_call: WrenchIcon,
  proposal: FileCheckIcon,
  note: StickyNoteIcon,
};
const STEP_LABELS: Record<RunStepRow["kind"], string> = {
  model_call: "Model call",
  tool_call: "Tool call",
  proposal: "Proposal",
  note: "Note",
};

const VERDICT_LABELS = {
  verified: ["Verified", "success"],
  not_verified: ["Not verified", "danger"],
  inconclusive: ["Inconclusive", "warning"],
} as const;

type Decision = RunDetail["decisions"][number];
type Pointer = NonNullable<RunDetail["output_ref"]>;

/** The run's verdict (ADR-0019 §1): one per run, written by the agent at the end. */
export function VerdictBadge({ run }: { run: Pick<RunRow, "verdict" | "state"> }) {
  if (run.verdict) {
    const [label, variant] = VERDICT_LABELS[run.verdict];
    return (
      <Badge variant={variant} data-slot="verdict">
        {label}
      </Badge>
    );
  }
  return (
    <span className="text-muted-foreground" data-slot="verdict">
      {isLive(run.state) ? "After the run" : "None"}
    </span>
  );
}

const POINTER_LABELS: Record<Pointer["kind"], string> = {
  run_step: "Step",
  object: "File",
  task: "Task",
  message: "Message",
  page: "Page",
  external_action: "External action",
  attachment: "Task file",
};

/** A pointer (never prose): a step by its number, a stored file by its name, else kind and id. */
function PointerText({ pointer, steps }: { pointer: Pointer; steps: RunStepRow[] }) {
  if (pointer.kind === "run_step") {
    const step = steps.find((s) => s.id === pointer.id);
    return <span>{step ? `Step ${step.seq}` : `Step ${pointer.id.slice(-8)}`}</span>;
  }
  if (pointer.kind === "object")
    return (
      <code className="font-mono text-xs break-all" title={pointer.id}>
        {pointer.id.split("/").at(-1)}
      </code>
    );
  return (
    <span>
      {POINTER_LABELS[pointer.kind]}{" "}
      <code className="font-mono text-xs" title={pointer.id}>
        {pointer.id.slice(-8)}
      </code>
    </span>
  );
}

/** The decision the resume writes (`VERDICT_RESET` of services/api domain/runs/decision-log.ts). */
const VERDICT_RESET = "verdict_reset";

const RESULT_LABELS = {
  ok: ["OK", "success"],
  failed: ["Failed", "danger"],
  unknown: ["Unknown", "neutral"],
} as const;

/**
 * A premise escalation's evidence as the log holds it (DC-07): the run's failed decisions — two
 * of them state the escalated assumption. Neither the assumption's text nor which two fixes it
 * named are in the log or the run (they travel on the `run.premise_escalated` event), and a
 * resume without a verdict leaves no mark in the log, so the page lists every failed decision
 * rather than guess which ones or claim a scope.
 */
export function failedDecisions(decisions: Decision[]): Decision[] {
  return decisions.filter((d) => d.result === "failed");
}

const OWN_MCP = "mcp__companyos__";

/**
 * Wave 12: the steps the timeline shows. A CompanyOS MCP call appears twice — the API's own
 * "MCP …" step and the agent CLI's copy (`mcp__companyos__<tool>`, then its result) — so the CLI's
 * copy and its paired result (same `call_id`) are left out. Other CLI tools stay.
 */
export function shownSteps(steps: RunStepRow[]): RunStepRow[] {
  const ours = new Set<string>();
  const out: RunStepRow[] = [];
  let lastWasOurs = false;
  for (const s of steps) {
    const p = s.payload;
    if (p.source === "cli" && typeof p.name === "string" && p.name.startsWith(OWN_MCP)) {
      if (typeof p.call_id === "string") ours.add(p.call_id);
      lastWasOurs = true;
      continue;
    }
    if (p.source === "cli" && p.type === "tool_result") {
      // Paired by id; a result without one (older runtimes) follows its call directly.
      const paired = typeof p.call_id === "string" ? ours.has(p.call_id) : lastWasOurs;
      lastWasOurs = false;
      if (paired) continue;
      out.push(s);
      continue;
    }
    lastWasOurs = false;
    out.push(s);
  }
  return out;
}

/** What a step did, from its payload (`summary`, else the tool), never the raw payload. */
export function stepText(s: RunStepRow): string {
  const p = s.payload;
  if (p.redacted === true) return "Hidden from people (agent-only content)";
  if (typeof p.summary === "string") return p.summary;
  // AGENT-KPI-TOOL: the API's own step for a KPI the run reported (`outcomes.record`).
  if (p.tool === "outcomes.record" && typeof p.metric_key === "string") {
    const what = p.kind === "event" ? `+${String(p.quantity ?? 1)} event` : `= ${String(p.value)}`;
    const dup =
      p.status === "duplicate"
        ? " (already recorded)"
        : p.status === "pending_review"
          ? " (waits for review)"
          : "";
    const why = typeof p.evidence_text === "string" ? ` · “${p.evidence_text}”` : "";
    return `outcomes.record: ${p.metric_key} ${what} for ${String(p.period_key)}${dup}${why}`;
  }
  if (typeof p.tool === "string") return p.tool;
  // AGENT-READY: what the runtime streams for a container run (services/agent-runtime): the
  // CLI's tool events carry `name` (a call) or `type: tool_result`; its own notes `source`.
  if (typeof p.name === "string" && p.name) return p.name;
  if (p.type === "tool_result") return "Tool result";
  if (p.source === "runtime" && typeof p.node === "string")
    return `Started on ${p.node}${typeof p.adapter === "string" ? ` (${p.adapter})` : ""}`;
  if (p.source === "runtime" && p.adapter_meta) return "Agent CLI started";
  // D14 preview: Recall did not answer, so the run read the agent's hot copy instead.
  if (p.source === "memory" && typeof p.stale_since === "string")
    return `Memory from the hot copy, stale since ${formatDateTime(p.stale_since)} (Recall did not answer)`;
  if (typeof p.output === "string") return "Agent output";
  if (typeof p.object_key === "string") return "Large payload, stored as an object";
  return STEP_LABELS[s.kind];
}

export function RunDetailScreen({
  api,
  company,
  id,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
}) {
  const run = useQuery(runQuery(api, company.id, id));
  const names = useDeNames(api, company.id);
  const currency = useCostCurrency(api, company.id);
  // MONITORING-P1: what the DE's host reported (none on a server without the route).
  const telemetry = useRunTelemetry(api, company.id, id);
  const host = telemetry.data;
  if (run.isError && run.error instanceof ApiError && run.error.status === 404)
    return <NotFoundPage message="No such run in this company, or you may not view it." />;
  const r = run.data;
  const name = r ? (names.get(r.membership_id) ?? "Digital employee") : "Run";
  return (
    <>
      <PageHeader
        eyebrow={
          <Link
            to="/c/$slug/agents/runs"
            params={{ slug: company.slug }}
            search={keepFocus}
            className="underline-offset-4 hover:underline"
          >
            Runs
          </Link>
        }
        title={r ? `${name}'s run` : "Run"}
        badge={r ? <RunStateBadge state={r.state} /> : null}
        subtitle={
          r
            ? `${wakeupSource(r.trigger_kind, r.trigger_ref)} · queued ${formatDateTime(r.created_at)}`
            : undefined
        }
        actions={
          r && isLive(r.state) ? (
            <>
              {host ? (
                <StopOnHostButton
                  api={api}
                  company={company}
                  runId={r.id}
                  live={host.stop_requests.find(stopIsLive)}
                />
              ) : null}
              <StopRun api={api} company={company} run={r} />
            </>
          ) : null
        }
      />
      {run.isPending ? (
        <Loading label="Loading the run" rows={4} />
      ) : run.isError ? (
        <LoadError what="The run" error={run.error} />
      ) : r ? (
        <RunBody api={api} company={company} run={r} name={name} currency={currency} host={host} />
      ) : null}
    </>
  );
}

/** AGENT-STABILITY: `MAX_ATTEMPTS` of services/api domain/runs/retry.ts. */
const MAX_ATTEMPTS = 3;

/** Why a run ended, for the reasons the platform itself writes. */
const END_REASON_LABELS: Record<string, string> = {
  runtime_lost: "The agent runtime lost the run",
  not_claimed: "No agent runtime picked the run up",
  timed_out: "It ran past its deadline",
  tool_loop: "Stopped: the same tool call failed three times in a row",
};

/** Why a lost run was not retried (`retry_reason`). */
const PARK_LABELS: Record<string, string> = {
  attempts_spent: `lost ${MAX_ATTEMPTS} times`,
  side_effects: "it had already acted, so it was not re-run",
  agent_gone: "its agent is gone",
  refused_budget: "the budget had no room for a retry",
  refused_paused: "the agent is paused",
  refused_policy: "admission refused the retry",
};

function RunLink({ company, id }: { company: CompanyAccess; id: string }) {
  return (
    <Link
      to="/c/$slug/agents/runs/$id"
      params={{ slug: company.slug, id }}
      search={keepFocus}
      className="text-primary-text underline-offset-4 hover:underline"
    >
      {id.slice(-8)}
    </Link>
  );
}

function RunBody({
  api,
  company,
  run: r,
  name,
  currency,
  host,
}: {
  api: Api;
  company: CompanyAccess;
  run: RunDetail;
  name: string;
  currency: string | undefined;
  /** MONITORING-P1: the host's telemetry, when the server has it. */
  host: RunTelemetry | undefined;
}) {
  const walletOn = useWalletOn(api, company.id);
  // A run from the chat twin's agent rooms may come without its log.
  const decisions = r.decisions ?? [];
  const steps = shownSteps(r.steps);
  const events = host?.events ?? [];
  const timeline = mergeTimeline(steps, events);
  const items = [
    {
      label: "Digital employee",
      value: (
        <Link
          to="/c/$slug/agents/de/$id"
          params={{ slug: company.slug, id: r.membership_id }}
          search={keepFocus}
          className="text-primary-text underline-offset-4 hover:underline"
        >
          {name}
        </Link>
      ),
    },
    {
      label: "Woken by",
      value: (
        <span className="inline-flex flex-wrap items-center gap-x-2">
          {wakeupSource(r.trigger_kind, r.trigger_ref)}
          {r.trigger_ref?.kind === "delegation_request" && r.trigger_ref.id ? (
            <Link
              to="/c/$slug/agents/delegations/$id"
              params={{ slug: company.slug, id: r.trigger_ref.id }}
              search={keepFocus}
              className="text-primary-text underline-offset-4 hover:underline"
            >
              View request
            </Link>
          ) : r.trigger_ref?.kind === "approval" && r.trigger_ref.id ? (
            <Link
              to="/c/$slug/agents/approvals/$id"
              params={{ slug: company.slug, id: r.trigger_ref.id }}
              search={keepFocus}
              className="text-primary-text underline-offset-4 hover:underline"
            >
              View approval
            </Link>
          ) : r.trigger_ref?.kind === "reporting_duty" && r.seat_id ? (
            // AGENT-REPORTING: the duty that woke the agent, in Setup › KPI reporting.
            <Link
              to="/c/$slug/setup/kpi-reporting"
              params={{ slug: company.slug }}
              search={
                ((prev: Record<string, unknown>) => ({
                  ...keepFocus(prev),
                  seat: r.seat_id,
                })) as never
              }
              className="text-primary-text underline-offset-4 hover:underline"
            >
              View duty
            </Link>
          ) : null}
        </span>
      ),
    },
    {
      label: "Cost",
      value: (
        <span className="inline-flex items-center gap-2">
          <CostChip run={r} currency={currency} hideTokens={walletOn} />
          {walletOn || usageUnreported(r) ? null : (
            <span className="text-muted-foreground tabular-nums">
              {formatTokens(r.tokens_in)} in · {formatTokens(r.tokens_out)} out
            </span>
          )}
        </span>
      ),
    },
    { label: "Verdict", value: <VerdictBadge run={r} /> },
    ...(r.verdict_evidence_ref
      ? [
          {
            label: "Verdict evidence",
            value: <PointerText pointer={r.verdict_evidence_ref} steps={r.steps} />,
          },
        ]
      : []),
    ...(r.output_ref
      ? [{ label: "Output", value: <PointerText pointer={r.output_ref} steps={r.steps} /> }]
      : []),
    {
      label: "Started",
      value: r.started_at ? formatDateTime(r.started_at) : "Not started (queued)",
    },
    {
      label: r.ended_at ? "Ran for" : "Running for",
      value: r.started_at ? formatElapsed(r.started_at, r.ended_at) : "—",
    },
    {
      label: "Budget reserved",
      value: isLive(r.state)
        ? formatMicros(r.budget_reserved_micros, currency)
        : "Settled at the run's cost",
    },
    { label: "Model", value: r.model ? `${r.model} · ${r.model_path}` : r.model_path },
    ...(r.state_reason
      ? [
          {
            label: "Reason",
            value:
              REVIEW_REASON_LABELS[r.state_reason] ??
              END_REASON_LABELS[r.state_reason] ??
              r.state_reason,
          },
        ]
      : []),
    // AGENT-STABILITY: a lost run's automatic successors (services/api domain/runs/retry.ts).
    ...(r.attempt > 1 || r.retry_state
      ? [
          {
            label: "Attempt",
            value: (
              <span className="inline-flex flex-wrap items-center gap-1" data-slot="run-attempt">
                {`${r.attempt} of ${MAX_ATTEMPTS}`}
                {r.retry_of_run_id ? (
                  <>
                    {" · retry of "}
                    <RunLink company={company} id={r.retry_of_run_id} />
                  </>
                ) : null}
              </span>
            ),
          },
        ]
      : []),
    ...(r.retry_state
      ? [
          {
            label: "Retry",
            value: (
              <span className="inline-flex flex-wrap items-center gap-1" data-slot="run-retry">
                {r.retry_state === "scheduled" ? (
                  `Retrying automatically${r.retry_after ? ` at ${formatDateTime(r.retry_after)}` : ""}`
                ) : r.retry_state === "spawned" && r.retried_by_run_id ? (
                  <>
                    {"Carried on by "}
                    <RunLink company={company} id={r.retried_by_run_id} />
                  </>
                ) : (
                  `Not retried: ${PARK_LABELS[r.retry_reason ?? ""] ?? r.retry_reason ?? "needs review"}. A person reviews it.`
                )}
              </span>
            ),
          },
        ]
      : []),
    ...(r.parent_run_id
      ? [
          {
            label: "Parent run",
            value: (
              <Link
                to="/c/$slug/agents/runs/$id"
                params={{ slug: company.slug, id: r.parent_run_id }}
                search={keepFocus}
                className="text-primary-text underline-offset-4 hover:underline"
              >
                {r.parent_run_id.slice(-8)}
              </Link>
            ),
          },
        ]
      : []),
    // DC-08: an ephemeral worker reads only what its delegation listed, under the DE's standing
    // orders copied verbatim from the deployed bundle.
    ...(r.context_refs
      ? [
          {
            label: "Worker scope",
            value: (
              <span className="inline-flex flex-wrap items-center gap-1" data-slot="worker-refs">
                {r.context_refs.length ? (
                  r.context_refs.map((ref) => (
                    <Badge key={`${ref.kind}:${ref.id}`} variant="neutral">
                      {ref.kind} {ref.id.slice(-8)}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground">No refs: its own run only</span>
                )}
              </span>
            ),
          },
          {
            label: "Standing orders",
            value: r.bundle_sha
              ? `Verbatim from the deployed bundle ${r.bundle_sha.slice(0, 12)}`
              : "—",
          },
        ]
      : []),
  ];
  return (
    <>
      {host ? <RunAlerts alerts={host.alerts} /> : null}
      {needsReview(r) ? (
        <ReviewNotice api={api} company={company} run={r} decisions={decisions} />
      ) : null}
      {failureStopped(r) ? (
        // FAILURE-STOP: the server stopped the run after repeated failures; Continue clears the
        // stop (or wakes the DE on the task again when the run has ended).
        <Notice
          tone="warning"
          className="mb-6"
          data-slot="run-failure-stop"
          title="Stopped after repeated failures"
          action={
            mayOfferResume(company) ? (
              <ContinueRunButton api={api} co={company.id} runId={r.id} size="sm" />
            ) : null
          }
        >
          <p>{r.failure_guard?.stopped?.reason}</p>
        </Notice>
      ) : null}
      <SettingsSection title="Run" icon={ActivityIcon} data-slot="run-facts">
        <Facts items={items} />
      </SettingsSection>
      {host ? <StopRequests requests={host.stop_requests} /> : null}
      {host ? <RunJobs jobs={host.jobs} currency={currency} /> : null}
      {r.state === "awaiting_approval" ? (
        <Section
          id="run-approval"
          title="Waiting for an approval"
          description="The run is parked until its proposal is decided."
        >
          <Link
            to="/c/$slug/agents/approvals"
            params={{ slug: company.slug }}
            search={keepFocus}
            className="self-start text-sm text-primary-text underline-offset-4 hover:underline"
          >
            Open the approvals queue
          </Link>
        </Section>
      ) : null}
      {r.hand_back ? (
        <Section id="run-hand-back" title="Hand-back">
          <div className="flex flex-col gap-2 text-sm" data-slot="hand-back">
            {r.hand_back.summary ? <p>{r.hand_back.summary}</p> : null}
            {r.hand_back.next_steps?.length ? (
              <ul className="list-disc pl-5 text-muted-foreground">
                {r.hand_back.next_steps.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            ) : null}
            {r.hand_back.needs && r.hand_back.needs !== "nothing" ? (
              <Badge variant="info">Needs {r.hand_back.needs}</Badge>
            ) : null}
          </div>
        </Section>
      ) : null}
      <Section
        id="run-steps"
        title="Steps"
        description={`${steps.length} step${steps.length === 1 ? "" : "s"}${
          events.length
            ? `, ${events.length} host event${events.length === 1 ? "" : "s"}${host?.truncated ? " (the first 500)" : ""}`
            : ""
        }, oldest first.`}
      >
        {timeline.length ? (
          <ol className="flex flex-col" data-slot="run-steps">
            {timeline.map((t) =>
              t.kind === "step" ? (
                <StepItem
                  key={t.step.id}
                  step={t.step}
                  currency={currency}
                  api={api}
                  co={company.id}
                />
              ) : (
                <EventItem key={t.event.id} event={t.event} currency={currency} />
              ),
            )}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">
            {r.state === "queued"
              ? "Queued: the runtime has not picked the run up yet."
              : "The run recorded no steps."}
          </p>
        )}
      </Section>
      <DecisionLog
        api={api}
        company={company}
        run={r}
        decisions={decisions}
        truncated={r.decisions_truncated ?? false}
      />
    </>
  );
}

/**
 * A run waiting for its supervisor (`needsReview`): why, and Resume for a member (the server
 * decides who may). A premise escalation lists the run's failed decisions.
 */
function ReviewNotice({
  api,
  company,
  run: r,
  decisions,
}: {
  api: Api;
  company: CompanyAccess;
  run: RunDetail;
  decisions: Decision[];
}) {
  const premise = r.state_reason === "premise_escalated";
  // Only from the whole log: a cut log may miss the last resume or the fixes after it.
  const partial = premise && (r.decisions_truncated ?? false);
  const fixes = premise && !partial ? failedDecisions(decisions) : [];
  const reason = REVIEW_REASON_LABELS[r.state_reason ?? ""] ?? r.state_reason;
  return (
    <Notice
      tone="warning"
      className="mb-6"
      data-slot="run-review"
      title={
        r.state_reason === "premise_escalated"
          ? "Premise escalated: waiting for review"
          : "Waiting for review"
      }
      action={
        mayOfferResume(company) ? (
          <ResumeRunButton api={api} co={company.id} runId={r.id} size="sm" />
        ) : null
      }
    >
      <div className="flex flex-col gap-1.5">
        <p>
          {reason}. The run is parked until its supervisor resumes it; after 72 hours in review it
          fails.
        </p>
        {partial ? (
          <p data-slot="premise-partial">
            The failed fixes it rests on are in the decision log below (show the whole log).
          </p>
        ) : fixes.length ? (
          <div>
            <p>Its failed decisions (two of them state the assumption it questions):</p>
            <ul className="list-disc pl-5" data-slot="premise-fixes">
              {fixes.map((d) => (
                <li key={d.id}>
                  #{d.seq} {d.decision}
                  {d.why ? <span className="text-muted-foreground"> — {d.why}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Notice>
  );
}

/**
 * The run's decision log (ADR-0019 §5), in `seq` order: what it decided, why, how it went and the
 * evidence. The detail carries the first 100 rows; a longer log loads on request, page by page.
 */
function DecisionLog({
  api,
  company,
  run: r,
  decisions,
  truncated,
}: {
  api: Api;
  company: CompanyAccess;
  run: RunDetail;
  decisions: Decision[];
  truncated: boolean;
}) {
  const [whole, setWhole] = useState(false);
  const all = useInfiniteQuery({ ...runDecisionsQuery(api, company.id, r.id), enabled: whole });
  const loaded = whole && all.data ? all.data.pages.flatMap((p) => p.data) : null;
  const rows = loaded ?? decisions;
  return (
    <Section
      id="run-decisions"
      title="Decision log"
      description={
        rows.length
          ? `${rows.length}${truncated && !loaded ? "+" : ""} decision${rows.length === 1 ? "" : "s"}, in order.`
          : undefined
      }
    >
      {truncated && !loaded ? (
        <Notice
          tone="info"
          className="mb-3"
          data-slot="log-truncated"
          action={
            <Button size="sm" variant="outline" onClick={() => setWhole(true)} disabled={whole}>
              {whole ? "Loading…" : "Show the whole log"}
            </Button>
          }
        >
          Showing the first {decisions.length} decisions; the log is longer.
        </Notice>
      ) : null}
      {whole && all.isError ? <LoadError what="The decision log" error={all.error} /> : null}
      {rows.length ? (
        <Table data-slot="decision-log">
          <TableCaption>Decision log of the run, by sequence number.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">#</TableHead>
              <TableHead scope="col">When</TableHead>
              <TableHead scope="col">Decision</TableHead>
              <TableHead scope="col">Why</TableHead>
              <TableHead scope="col">Result</TableHead>
              <TableHead scope="col">Evidence</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((d) => (
              <DecisionRow key={d.id} decision={d} steps={r.steps} />
            ))}
          </TableBody>
        </Table>
      ) : (
        <p className="text-sm text-muted-foreground">
          {isLive(r.state) ? "No decisions recorded yet." : "The run recorded no decisions."}
        </p>
      )}
      {loaded ? (
        <LoadMore
          what="decisions"
          hasMore={all.hasNextPage}
          loading={all.isFetchingNextPage}
          onMore={() => void all.fetchNextPage()}
        />
      ) : null}
    </Section>
  );
}

function DecisionRow({ decision: d, steps }: { decision: Decision; steps: RunStepRow[] }) {
  const escalation = d.escalation_kind === "premise";
  const [label, variant] = RESULT_LABELS[d.result];
  return (
    <TableRow
      data-seq={d.seq}
      data-escalation={escalation ? d.escalation_kind : undefined}
      className={escalation ? "bg-warning-soft" : undefined}
    >
      <TableCell className="text-muted-foreground tabular-nums">{d.seq}</TableCell>
      <TableCell className="text-muted-foreground tabular-nums">
        {formatDateTime(d.decided_at)}
      </TableCell>
      <TableHead scope="row" className="font-medium whitespace-normal">
        <span className="flex flex-wrap items-center gap-2">
          {escalation ? <Badge variant="warning">Premise escalation</Badge> : null}
          {d.decision === VERDICT_RESET ? "Resumed: verdict cleared" : d.decision}
        </span>
      </TableHead>
      <TableCell className="whitespace-normal text-muted-foreground">{d.why}</TableCell>
      <TableCell>
        <Badge variant={variant}>{label}</Badge>
      </TableCell>
      <TableCell>
        {d.evidence_ref ? <PointerText pointer={d.evidence_ref} steps={steps} /> : "—"}
      </TableCell>
    </TableRow>
  );
}

/**
 * AGENT-POLISH: the approval a step's post was held for (the runtime's `post_message` result
 * names it), or null. The step itself is append-only; its post's state is read live.
 */
export function heldApprovalOf(s: RunStepRow): string | null {
  const p = s.payload;
  if (p.name !== "post_message" || typeof p.result !== "string") return null;
  const m = /"approval_id":"([0-9a-f-]{36})"/i.exec(p.result);
  return m?.[1] ?? null;
}

const HELD_POST: Record<string, [string, "warning" | "success" | "danger" | "info" | "neutral"]> = {
  pending: ["Awaiting approval", "warning"],
  approved: ["Approved · posted", "success"],
  edited: ["Approved with edits · posted", "success"],
  rejected: ["Rejected · not posted", "danger"],
  expired: ["Expired · not posted", "neutral"],
};

/** The held post's state now (refetched by the approval's change pings). */
function HeldPostBadge({ api, co, id }: { api: Api; co: string; id: string }) {
  const a = useQuery(approvalQuery(api, co, id));
  const state = a.data?.state;
  if (!state) return null;
  const [label, tone] = HELD_POST[state] ?? [state, "neutral"];
  return (
    <Badge variant={tone} data-slot="held-post" data-approval-state={state}>
      {label}
    </Badge>
  );
}

function StepItem({
  step: s,
  currency,
  api,
  co,
}: {
  step: RunStepRow;
  currency: string | undefined;
  api: Api;
  co: string;
}) {
  const Icon = STEP_ICONS[s.kind];
  const failed = s.payload.error === true;
  const [open, setOpen] = useState(false);
  const redacted = s.payload.redacted === true;
  const walletOn = useWalletOn(api, co);
  const held = heldApprovalOf(s);
  return (
    <li
      className="relative flex gap-3 border-l border-border pb-4 pl-5 last:pb-0"
      data-step={s.seq}
      data-kind={s.kind}
    >
      <span className="absolute top-0 -left-3 flex size-6 items-center justify-center rounded-full border border-border bg-card">
        <Icon className="size-3.5 text-muted-foreground" aria-hidden="true" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">
            {s.seq}. {STEP_LABELS[s.kind]}
          </span>
          {failed ? <Badge variant="danger">Error</Badge> : null}
          {held ? <HeldPostBadge api={api} co={co} id={held} /> : null}
          <span className="text-xs text-muted-foreground tabular-nums">
            {formatDateTime(s.created_at)}
            {s.duration_ms !== null ? ` · ${(s.duration_ms / 1000).toFixed(1)} s` : ""}
            {!walletOn && s.model ? ` · ${s.model}` : ""}
            {!walletOn && (s.tokens_in || s.tokens_out)
              ? ` · ${formatTokens(s.tokens_in)}/${formatTokens(s.tokens_out)} tokens`
              : ""}
            {/* A step has no wallet charge of its own: wallet view shows no step cost. */}
            {!walletOn && s.cost_micros ? ` · ${formatMicros(s.cost_micros, currency)}` : ""}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{stepText(s)}</p>
        {redacted ? null : (
          <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger
              render={<Button variant="ghost" size="xs" className="self-start" />}
            >
              <ChevronDownIcon aria-hidden="true" />
              {open ? "Hide payload" : "Show payload"}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <pre className="mt-1 max-h-64 overflow-auto rounded-md bg-sunken p-3 font-mono text-xs">
                {JSON.stringify(s.payload, null, 2)}
              </pre>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </li>
  );
}

/** Stop a live run (`runs.cancel`): releases or settles its budget reservation. */
function StopRun({ api, company, run }: { api: Api; company: CompanyAccess; run: RunRow }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const stop = useMutation({
    mutationFn: () => api.client.runs.cancel({ params: { co: company.id, id: run.id } }),
    onSettled: () => invalidateAgents(qc, company.id),
  });
  return (
    <>
      <Button variant="destructive" onClick={() => setOpen(true)} disabled={stop.isPending}>
        <OctagonXIcon aria-hidden="true" />
        {stop.isPending ? "Stopping…" : "Stop run"}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="inline-flex items-center gap-1.5">
              Stop this run?
              {/* WF-FIX: Stop ends the CompanyOS run, not a self-hosted DE's container. */}
              <InfoTip label="About stopping a run">
                <span data-slot="stop-container-note">
                  Ends the run in CompanyOS. A self-hosted DE's Hermes container keeps running.
                </span>
              </InfoTip>
            </AlertDialogTitle>
            <AlertDialogDescription>
              The digital employee stops at its next step. What it spent so far is kept; the rest of
              its budget reservation is released.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep running</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                stop.mutate();
                setOpen(false);
              }}
            >
              Stop run
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
